import React, { FormEvent, useMemo, useRef } from 'react';
import './Promotions.scss';

import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import { Card } from 'primereact/card';
import { FormField } from '../../components/FormField/FormField';
import { RichTextEditor, RichTextEditorHandle } from '../../components/RichTextEditor/RichTextEditor';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { useForm } from '../../hooks/useForm';
import { classNames } from 'primereact/utils';
import { ExternalUsersSelect } from '../../components/ExternalUsersSelect/ExternalUsersSelect';
import { useClient } from '../../hooks/useClient';
import { Email } from '../../types/email';
import { PROMOTIONS_FORM_VALIDATORS, PromotionsForm } from './validators';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { ExternalUser } from '../../types/externalUser';

const EMPTY_PROMOTIONS_FORM = {
  users: [],
  subject: '',
  content: '',
};

export const Promotions = () => {
  const { post } = useClient();
  const { organization } = useOrganizationContext();

  const { form, setForm, setFormFields, hasChanged, isSaving, setIsSaving, validate, validationErrors } = useForm<PromotionsForm>(
    EMPTY_PROMOTIONS_FORM,
    true,
    PROMOTIONS_FORM_VALIDATORS,
  );

  const editorRef = useRef<RichTextEditorHandle>(null);

  const userIds = useMemo(() => form.users.map((_) => _.id), [form.users]);

  const sendPromotionEmail = async () => {
    const errors = validate();
    if (!form || errors) return;
    setIsSaving(true);
    const { content, subject, users } = form;

    const email: Email = {
      to: users.filter((_): _ is ExternalUser & { email: string } => !!_.email).map((_) => _.email),
      subject,
      html: content,
      senderOrganizationId: organization?.id,
    };

    // `post` never throws and returns nothing for `/email`, so only the success handler can tell a sent message from a failed one.
    let sent = false;
    await post('/email', {
      body: email,
      handlers: {
        defaultSuccess: () => {
          sent = true;
        },
      },
      successMessage: {
        summary: 'Mensaje enviado',
        detail: 'El mensaje se envió correctamente a todos los destinatarios',
      },
      errorMessages: {
        summary: 'Error al enviar el mensaje',
        defaultDetail: 'No se ha podido enviar el mensaje. Inténtalo de nuevo.',
      },
    });

    if (sent) {
      setForm(EMPTY_PROMOTIONS_FORM);
      // The editor owns its content: empty it with the form (#524).
      editorRef.current?.setContent(EMPTY_PROMOTIONS_FORM.content);
    }
    setIsSaving(false);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendPromotionEmail();
  };

  return (
    <PageTemplate className='promotions-page' title='Promociones'>
      <Card>
        <form
          className={classNames({
            'form-loading': isSaving,
          })}
          onSubmit={handleSubmit}
        >
          <FormField
            labelTitle='Usuarios'
            elementId='users-dropdown'
            required
            error={validationErrors?.users as unknown as string}
          >
            <ExternalUsersSelect
              id='users-dropdown'
              isSingle={false}
              value={userIds}
              onChange={(selectedUsers) => setFormFields({ users: selectedUsers })}
              placeholder='Elige a qué usuarios contactar'
              className={classNames({ 'p-invalid': validationErrors?.users })}
              error={validationErrors?.users}
              filterOnlyWithEmail
            />
          </FormField>
          <FormField fullWidth labelTitle='Asunto' elementId='subject' required error={validationErrors?.subject}>
            <InputText
              id='subject'
              placeholder='Asunto del correo de la promoción'
              value={form.subject}
              onChange={(e) =>
                setFormFields({
                  subject: e.target.value,
                })
              }
              className={classNames({ 'p-invalid': validationErrors?.subject })}
            />
          </FormField>
          <FormField fullWidth labelTitle='Contenido' elementId='content' required error={validationErrors?.content}>
            <RichTextEditor
              ref={editorRef}
              id='content'
              initialValue={form.content}
              onChange={(value) => setFormFields({ content: value })}
              placeholder='Contenido de la promoción'
              className={classNames({ 'p-invalid': validationErrors?.content })}
              useBase64Images
            />
          </FormField>
          <Button
            className='pull-right'
            label='Enviar'
            icon={PrimeIcons.SEND}
            disabled={!hasChanged}
            loading={isSaving}
            type='submit'
          />
        </form>
      </Card>
    </PageTemplate>
  );
};
